import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PMAvatar, PMCard, PMFilterBar, PMKanban, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { usePeople } from "@/data/queries";
import { Page } from "@/ui/Page";
import { Kpi } from "@/ui/Kpi";
import { Character } from "@/ui/Character";
import { avatarColor, fullName, relTime } from "@/ui/format";
import { usePipeline, type PipeFarm, type Stage } from "./queries";
import "./dashboards.css";

const DAY = 86_400_000;
// Stage tone → token. Stored v1 colours may be hex; v2 never renders them, it
// maps the stage's keyword (or its position) onto the semantic palette.
const TONES: Record<string, string> = { info: "var(--pm-info)", warning: "var(--pm-warning)", note: "var(--pm-data-note)", success: "var(--pm-success)", neutral: "var(--pm-fg-3)" };
const ORDER = ["info", "warning", "note", "success", "neutral"];
const toneFor = (s: Stage, i: number) => TONES[s.colour ?? ""] ?? TONES[ORDER[i % ORDER.length]];
const isWon = (s?: Stage | null) => /active|won|client|customer/i.test(s?.name ?? "");
const isLost = (s?: Stage | null) => /lost|closed|churn|inactive/i.test(s?.name ?? "");

// Sales pipeline (core-features §4.3): top-level locations grouped by status.
// Moving a card between stages needs PATCH /map_features/:id (docs/api-contract/
// 02-map-features.md) wired through mutations.ts; read-only until then.
export function PipelinePage() {
  const search = useSearch({ from: "/t/$teamId/dashboards/pipeline" });
  const nav = useNavigate();
  const { teamId, hierarchy } = useApp();
  const top = hierarchy.levels[0];
  const people = usePeople();
  const pipe = usePipeline(top.key);
  const { farms, loading } = pipe;
  // Funnel order by stage meaning (lead → booked → proposal → won → lost); unknown names keep their stored order.
  const rank = (n?: string | null) => { const t = (n ?? "").toLowerCase(); return /lead|prospect|new/.test(t) ? 0 : /book|visit|meeting|qualif/.test(t) ? 1 : /propos|quote|negoti/.test(t) ? 2 : /active|won|client|customer/.test(t) ? 3 : /lost|closed|churn|inactive/.test(t) ? 5 : 4; };
  const stages = [...pipe.stages].map((s, i) => ({ s, i })).sort((a, b) => rank(a.s.name) - rank(b.s.name) || a.i - b.i).map((x) => x.s);
  const who = search.who;
  const set = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: true } as any);

  const rows = useMemo(() => farms.filter((f) => !who || f.responsible_user_id === who), [farms, who]);
  const stale = (f: PipeFarm) => !f.last_visit || Date.now() - new Date(f.last_visit).getTime() > 30 * DAY;
  const stageOf = (f: PipeFarm) => stages.find((s) => s.id === f.status_id) ?? null;
  const active = rows.filter((f) => isWon(stageOf(f))).length;
  const lost = rows.filter((f) => isLost(stageOf(f))).length;
  const open = rows.filter((f) => !isWon(stageOf(f)) && !isLost(stageOf(f)));
  const conversion = rows.length - lost ? Math.round((active / (rows.length - lost)) * 100) : 0;
  const staleLeads = open.filter(stale).length;
  const unstaged = rows.filter((f) => !stageOf(f));

  const columns = [
    ...stages.map((s, i) => ({ id: s.id, title: s.name ?? "Untitled", color: toneFor(s, i), cards: rows.filter((f) => f.status_id === s.id).map((f) => ({ ...f, id: f.id, title: f.label ?? "", stripe: toneFor(s, i) })) })),
    ...(unstaged.length ? [{ id: "__none", title: "No status", color: "var(--pm-fg-3)", cards: unstaged.map((f) => ({ ...f, id: f.id, title: f.label ?? "", stripe: "var(--pm-fg-3)" })) }] : []),
  ];
  const whoName = who ? fullName(people.data.find((p) => p.user_id === who)) : null;

  return (
    <Page chip="DASHBOARDS" title="Sales pipeline" subtitle={`${rows.length} ${rows.length === 1 ? top.label.toLowerCase() : top.plural.toLowerCase()} across ${stages.length} stages${whoName ? ` · ${whoName}` : ""}`} wide>
      <div className="kpis">
        <Kpi emoji={top.emoji} tone="tone-brand" value={rows.length} label={`${top.plural} in pipeline`} />
        <Kpi emoji="🤝" tone="tone-text" value={active} label="Active clients" />
        <Kpi emoji="📈" tone="tone-photo" value={conversion} suffix="%" label="Conversion (excl. lost)" />
        <Kpi emoji="⏳" tone="tone-warn" value={staleLeads} label="Open, no visit in 30 days" />
      </div>

      <PMFilterBar
        filters={[{ key: "who", label: "Owner", icon: "user-outline", searchable: true, value: who, options: people.data.map((p) => ({ value: p.user_id, label: fullName(p) })), onChange: (v: string | string[]) => set({ who: (v as string) || undefined }) }]}
        activeChips={whoName ? [{ key: "who", label: whoName }] : []}
        onRemoveChip={() => set({ who: undefined })}
        onClearAll={() => set({ who: undefined })}
      />

      {loading && !farms.length ? <PMSkeleton shape="rect" height={360} radius={16} /> : !stages.length ? (
        <Character name="cow" size={100} title="No pipeline stages yet" line={`Stages come from the team's ${top.label.toLowerCase()} statuses, set up in classic.`} />
      ) : (
        <>
          <PMCard eyebrow="Stage mix" icon="bar-chart-outline">
            <div className="pipe-funnel" role="img" aria-label={columns.map((c) => `${c.title} ${c.cards.length}`).join(", ")}>
              {columns.filter((c) => c.cards.length).map((c) => <i key={c.id} title={`${c.title}: ${c.cards.length}`} style={{ flex: c.cards.length, background: c.color }} />)}
            </div>
            <ul className="dv-legend" style={{ marginTop: 10, marginBottom: 0 }}>
              {columns.map((c) => <li key={c.id}><i style={{ background: c.color }} />{c.title} · {c.cards.length}</li>)}
            </ul>
          </PMCard>
          <PMKanban
            columns={columns}
            maxHeight={620}
            onCardClick={(card: any) => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: card.id } })}
            renderCard={(card: any) => {
              const f = card as PipeFarm;
              const owner = people.data.find((p) => p.user_id === f.responsible_user_id);
              return (
                <div className="pipe-card">
                  <div className="pipe-card-title">{top.emoji} {f.label}</div>
                  <div className="pipe-card-meta"><PMAvatar name={fullName(owner)} size={20} color={avatarColor(f.responsible_user_id)} />{owner ? fullName(owner) : "Unassigned"}</div>
                  <div className="pipe-card-foot">
                    <span className={`pipe-chip ${stale(f) ? "pipe-chip--stale" : ""}`}>{f.last_visit ? `Visited ${relTime(f.last_visit)}` : "Never visited"}</span>
                    {f.open_tasks > 0 && <span className="pipe-chip">{f.open_tasks} open {f.open_tasks === 1 ? "task" : "tasks"}</span>}
                  </div>
                </div>
              );
            }}
          />
          <p className="pipe-note">Stages are read from the team's {top.label.toLowerCase()} statuses. Change a stage from the phone app or classic; moving cards here comes with the web API.</p>
        </>
      )}
    </Page>
  );
}
